// Router
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
// Analytics
import { trackHeapEvent } from '@/util/analytics'
import { logUserActivity } from '@/util/user-analytics'
// Store
import $store from '../store/index'
// Pages
import Actor from '../pages/Actor.vue'
import FileDetail from '../pages/FileDetail.vue'
import ActorChanges from '../pages/ActorChanges.vue'
import ActorDetailVersionChooser from '../pages/ActorDetail/ActorDetailVersionChooser.vue'
import FoodleapSimplifiedActorDetail from '../pages/ActorDetail/FoodleapSimplifiedActorDetail.vue'
import ActorScores from '../pages/ActorScores.vue'
import AddActor from '../pages/AddActor.vue'
import AnnouncementDetailSimplifiedNew from '../pages/Simplified/AnnouncementDetailSimplifiedNew.vue'
import ChallengeDetailNew from '../pages/Simplified/ChallengeDetailNew.vue'
import ChangePassword from '../pages/ChangePassword.vue'
import CommunityDetail from '../pages/CommunityDetail/CommunityDetail.vue'
import Dashboards from '../pages/Dashboards.vue'
import Datalab from '../pages/Datalab.vue'
import Explore from '../pages/Explore.vue'
import ProductGallery from '../pages/Products.vue'
import ActorsSimplified from '../pages/Simplified/ActorsSimplified.vue'
import IntelligenceSimplified from '../pages/Simplified/IntelligenceSimplified.vue'
import IntelligenceDetail from '../pages/Simplified/IntelligenceDetail.vue'
import ForgotPassword from '../pages/ForgotPassword.vue'
import Info from '../pages/Info.vue'
import KitchenSink from '../pages/KitchenSink.vue'
import Notifications from '../pages/Notifications.vue'
import AnnouncementsNew from '../pages/Announcements.vue'
import Profile from '../pages/Profile.vue'
import Portfolios from '../pages/Portfolios.vue'
import Restricted from '../pages/Restricted.vue'
import Offline from '../pages/Offline.vue'
import Settings from '../pages/Settings.vue'
import SignIn from '../pages/SignIn.vue'
import SignUp from '../pages/SignUp.vue'
import SpottingAreaDetail from '../pages/Simplified/SpottingAreaDetail.vue'
import GdprTerms from '../pages/GdprTerms.vue'
import ConceptMap from '../components/Dashboard/ConceptMap.vue'
import InstantMonitoring from '../components/Dashboard/InstantMonitoring/InstantMonitoring.vue'
import SortablePortfolioDetail from '../pages/SortablePortfolioDetail.vue'
import Challenges from '../pages/Challenges.vue'
import EventsNew from '../pages/Events.vue'
import Policy from '../pages/Policy.vue'
import KnowledgeBaseDetail from '@/Domain/KnowledgeBase/Components/KnowledgeBaseDetail.vue'
import EcosystemMapDetail from '../components/Settings/EcosystemMapDetail.vue'
import ChatbotWidgetDetail from '../components/Settings/ChatbotWidgetDetail.vue'

// API
import { Portfolios as fetchPortfolio } from '../api/portfolios'
import { SiteTemplate } from '../store/modules/config'
import { ACTION_TYPES as USER_ACTION_TYPES } from '../store/modules/user.js'
import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../store/modules/ui.js'
import Communities from '../pages/Communities.vue'
import SpottingAreas from '../pages/SpottingAreas.vue'
import ActorScouting from '../components/Dashboard/ActorScouting.vue'
import MODAL_IDS from '@/constants/modal-ids'
import KnowledgeBaseCreate from '@/Domain/KnowledgeBase/Components/KnowledgeBaseCreate.vue'
import { getPost } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
import TransitionPulseDetail from '../pages/TransitionPulseDetail.vue'
import InnovationRadarDetail from '../pages/InnovationRadarDetail.vue'
import DeepDiveReportDetail from '../pages/DeepDiveReportDetail.vue'
import DeepDiveReportBalancedProtein from '../pages/DeepDiveReportBalancedProtein.vue'
import TransitionReportCheckout from '../pages/TransitionReportCheckout.vue'

// Static Custom Home Pages
import StaticHomeFoodleapAbout from '../components/Dashboard/StaticHomeFoodleapAbout.vue'
import StaticHomeFoodleapTransitions from '../components/Dashboard/StaticHomeFoodleapTransitions.vue'
import StaticHomeFoodleapFacilityFinder from '@/components/Dashboard/StaticHomeFoodleapFacilityFinder.vue'
import StaticHomeFoodleapDeepDivePage from '@/components/Dashboard/StaticHomeFoodleapDeepDivePage.vue'
import StaticHomeFoodleapPricing from '@/components/Dashboard/StaticHomeFoodleapPricing.vue'
import StaticHomeFoodleapTransitionPulsePage from '@/components/Dashboard/StaticHomeFoodleapTransitionPulsePage.vue'
import StaticHomeFoodleapInnovationRadarPage from '@/components/Dashboard/StaticHomeFoodleapInnovationRadarPage.vue'
import StaticHomeFoodleapTransitionLanding from '@/components/Dashboard/StaticHomeFoodleapTransitionLanding.vue'
import StaticHomeBrusselsDirectory from '@/components/Dashboard/StaticHomeBrusselsDirectory.vue'
import StaticHomeBrusselsHubs from '@/components/Dashboard/StaticHomeBrusselsHubs.vue'
import StaticHomeBrusselsInsights from '@/components/Dashboard/StaticHomeBrusselsInsights.vue'
import StaticHomeBrusselsAbout from '@/components/Dashboard/StaticHomeBrusselsAbout.vue'
import StaticHomeBrusselsJourneys from '@/components/Dashboard/StaticHomeBrusselsJourneys.vue'
import StaticHomeBrusselsHubsOverview from '@/components/Dashboard/StaticHomeBrusselsHubsOverview.vue'
import StaticHomeBrusselsEvents from '@/components/Dashboard/StaticHomeBrusselsEvents.vue'
import StaticHomeSamergoNetwork from '@/components/Dashboard/StaticHomeSamergoNetwork.vue'
import ActorsSimplifiedRegio from '@/pages/Simplified/ActorsSimplifiedRegio.vue'

function mustLogin (to, from, next) {
  if ($store.getters.hasAccessToPublisher && !!$store.state.config.allow_anonymous && !window.oauth_request) {
    if (!$store.getters.isLoggedIn) {
      $store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
    }

    next()
  } else {
    $store.getters.isLoggedIn ? next() : next('/login')
  }
}

function isDatascoutsEmployee (to, from, next) {
  if ($store.getters.isUserOfDataScoutsTeam) {
    next()
  } else {
    next('/')
  }
}

// On Foodleap the augmented profile is the actor detail page, so this route is just an alias for
// it and needs no guard. Elsewhere it is still the internal preview behind the email allowlist.
function canAccessAugmentedProfile (to, from, next) {
  $store.getters.isFoodleap || $store.getters.canPreviewAugmentedProfile ? next() : next('/')
}

function canAccessFoodleapReports (to, from, next) {
  $store.getters.canAccessTransitionReports ? next() : next('/')
}

// Radar & Deep Dive reports require a claimed actor; block navigation and show the gate modal instead
function mustHaveClaimedActor (to, from, next) {
  const claims = $store.getters.userClaims || []
  if ($store.getters.isMember || $store.getters.isPortfolioMember || claims.some(claim => claim.actor_type !== 'Product')) {
    next()
    return
  }
  $store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actorRequiredVariant: 'reports' })
  $store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ACTOR_CLAIM_REQUIRED)
  // Stay on the current page when navigating in-app; fall back to home on a direct page load
  next(from.matched.length ? false : '/')
}

function hasAccessToCommunities (to, from, next) {
  if ($store.getters.hasAccessToCommunities) {
    next()
  } else {
    next('/')
  }
}

function canViewKnowledgeBasePost (to, from, next) {
  if ($store.getters.canViewDetailKnowledgeBaseContentTypes ||
    $store.getters.canViewPreviewKnowledgeBaseContentTypes ||
    $store.getters.canViewAllKnowledgeBaseContentTypes
  ) {
    next()
    return
  }

  next('/knowledge-base')
}

async function canEditKnowledgeBasePost (to, from, next) {
  // First check if user is logged in
  if (!$store.getters.isLoggedIn) {
    next('/login')
    return
  }

  try {
    const documentId = to.params.documentId
    const communityId = to.params.communityId || null
    const spottingAreaId = to.params.spottingAreaId || null

    // Fetch the post to check permissions
    const response = await getPost(documentId, communityId, spottingAreaId)
    const post = response

    if (!post) {
      next('/knowledge-base')
      return
    }

    // Check if user can edit this post
    const currentUserId = Number($store.getters.userId)
    const creatorId = post.creator?.id || post.creator_id

    // User is the creator
    if (currentUserId === creatorId) {
      next()
      return
    }

    // User is owner or team member
    if ($store.getters.isOwner || $store.getters.isMember) {
      next()
      return
    }

    // Check if user has upload permissions (optional additional check)
    if ($store.getters.canUploadContentToKnowledgeBase) {
      next()
      return
    }

    // Check if user is an explicit contributor or community manager of this post
    try {
      const { fetchContributors } = await import('@/api/knowledge-base')
      const contributorData = await fetchContributors(Number(documentId))
      const contributorIds = (contributorData.contributors || []).map((c: any) => Number(c.id))
      const communityManagerIds = (contributorData.community_managers || []).map((m: any) => Number(m.id))

      if (contributorIds.includes(currentUserId) || communityManagerIds.includes(currentUserId)) {
        next()
        return
      }
    } catch (e) {
      // Contributor check failed — fall through to redirect
    }

    // If none of the above, redirect to post detail
    if (communityId) {
      next(`/communities/${communityId}/knowledge-base/posts/${documentId}`)
    } else if (spottingAreaId) {
      next(`/spotting-areas/${spottingAreaId}/knowledge-base/posts/${documentId}`)
    } else {
      next(`/knowledge-base/posts/${documentId}`)
    }
  } catch (error) {
    console.error('Error checking post edit permissions:', error)
    next('/knowledge-base')
  }
}

function defaultSettingsPage (to, form, next) {
  $store.getters.isOwner ? next('/settings/ecosystem') : next('/settings/users')
}

// Take a portfolio where the user has explore or contribute access to and set it as the active portfolio
function setExplorePortfolio () {
  const listOfPortfolios = []

  if ($store.state.managePortfolio.activePortfolio.id === undefined) {
    // Fetch all the portfolios that the user can contribute to
    if ($store.getters.userContributionPortfolios && $store.getters.userContributionPortfolios.length > 0) {
      $store.getters.userContributionPortfolios.forEach(portfolio => {
        if (!listOfPortfolios.includes(portfolio)) {
          listOfPortfolios.push(portfolio)
        }
      })
    }

    // Fetch all the portfolios that the user can explore
    if ($store.getters.userExplorePortfolios && $store.getters.userExplorePortfolios.length > 0) {
      $store.getters.userExplorePortfolios.forEach(portfolio => {
        if (!listOfPortfolios.includes(portfolio)) {
          listOfPortfolios.push(portfolio)
        }
      })
    }

    const localStoragePortfolio = window.localStorage.getItem('portfolio')

    if (localStoragePortfolio && localStoragePortfolio !== 'null') {
      const portfolio = localStoragePortfolio.replaceAll('"', '')

      fetchPortfolio.get(portfolio).then(portfolio => {
        $store.commit('FILTERS/SET_PORTFOLIO', portfolio.id)
        $store.commit('PORTFOLIOS/SET_ACTIVE_PORTFOLIO', portfolio)
      })
    } else {
      if (listOfPortfolios.length > 0) {
        // If there are portfolios that the user can either contribute or explore, the first portfolio of the created list will be fetched and set
        fetchPortfolio.get(listOfPortfolios[0]).then(portfolio => {
          $store.commit('FILTERS/SET_PORTFOLIO', portfolio.id)
          $store.commit('PORTFOLIOS/SET_ACTIVE_PORTFOLIO', portfolio)
        })
      }
    }
  }
}

const routes: Array<RouteRecordRaw> = [
  { path: '/', redirect: () => $store.getters.home },
  { path: '/actors', name: 'explore', component: Explore },
  { path: '/actors-simplified', name: 'actors-simplified', component: ActorsSimplified },
  { path: '/communities', name: 'communities', component: Communities, beforeEnter: hasAccessToCommunities },
  { path: '/communities/:communityId/:section?/:id?', name: 'community-detail', component: CommunityDetail, beforeEnter: hasAccessToCommunities },
  { path: '/products-simplified', name: 'products-simplified', component: ActorsSimplified },
  { path: '/intelligence-simplified', name: 'intelligence-simplified', component: IntelligenceSimplified },
  { path: '/intelligence-simplified/:id', name: 'intelligence-detail', component: IntelligenceDetail },
  { path: '/announcements-simplified', name: 'announcements-simplified', component: AnnouncementsNew },
  { path: '/events-simplified', name: 'events-simplified', component: EventsNew },
  { path: '/events', name: 'events', component: EventsNew },
  { path: '/events/:id', name: 'events-detail', component: AnnouncementDetailSimplifiedNew },
  { path: '/product-gallery', name: 'product-gallery', component: ProductGallery },
  { path: '/actors/create', name: 'addActor', component: AddActor, beforeEnter: mustLogin },
  { path: '/files/:fileId/:viewType?/:section?', name: 'fileDetail', component: FileDetail, beforeEnter: mustLogin },
  { path: '/admin', redirect: '/datalab' },
  { path: '/analytics', redirect: '/dashboards/analytics' },
  { path: '/dashboards', redirect: '/dashboards/analytics' },
  { path: '/dashboards/instant-monitoring/:id?', name: 'InstantMonitoring', component: InstantMonitoring },
  { path: '/dashboards/actor-scouting', name: '/dashboards/actor-scouting', component: ActorScouting },
  { path: '/dashboards/concept-map', redirect: '/dashboards/explore-content' },
  { path: '/dashboards/concept-map/:id', redirect: '/dashboards/concept-map/:id/content' },
  { path: '/dashboards/concept-map/:id/:tab?/:sub?', name: 'ConceptMap', component: ConceptMap },
  {
    path: '/about', name: 'foodleap-about', component: StaticHomeFoodleapAbout, beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/transitions', name: 'foodleap-transitions', component: StaticHomeFoodleapTransitions, beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/transitions/:slug', name: 'foodleap-transition-landing', component: StaticHomeFoodleapTransitionLanding, beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/facilities',
    name: 'foodleap-facility-finder',
    component: StaticHomeFoodleapFacilityFinder,
    beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/intelligence',
    name: 'foodleap-intelligence',
    component: StaticHomeFoodleapPricing,
    beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/transition-pulse-report',
    name: 'foodleap-transition-pulse',
    component: StaticHomeFoodleapTransitionPulsePage,
    beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/innovation-radar-report',
    name: 'foodleap-innovation-radar-report',
    component: StaticHomeFoodleapInnovationRadarPage,
    beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/deep-dive-report', name: 'foodleap-deep-dive', component: StaticHomeFoodleapDeepDivePage, beforeEnter: (to, from, next) => {
      if (!$store.getters.isFoodleap) return next('/')
      if ($store.getters.isLoggedIn) return next('/deep-dive-reports/balanced-protein-futures')
      next()
    },
  },
  { path: '/transition-reports/trial', name: 'transition-reports-trial', component: TransitionReportCheckout, beforeEnter: mustLogin },
  {
    path: '/deep-dive-report/download/:token',
    name: 'foodleap-deep-dive-download',
    component: StaticHomeFoodleapDeepDivePage,
    beforeEnter: (to, from, next) => {
      $store.getters.isFoodleap ? next() : next('/')
    },
  },
  {
    path: '/hubs-brussels/:id',
    name: 'brussels-hubs-overview',
    component: StaticHomeBrusselsHubs,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/hubs-brussels/:id',
    name: 'brussels-hubs',
    component: StaticHomeBrusselsHubs,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/partners-brussels/:id',
    name: 'brussels-partners',
    component: StaticHomeBrusselsHubs,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/directory-brussels',
    name: 'brussels-directory',
    component: StaticHomeBrusselsDirectory,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/insights-brussels',
    name: 'brussels-insights',
    component: StaticHomeBrusselsInsights,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/about-brussels',
    name: 'brussels-about',
    component: StaticHomeBrusselsAbout,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/journeys-brussels',
    name: 'brussels-journeys',
    component: StaticHomeBrusselsJourneys,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/hubs-overview-brussels',
    name: 'brussels-hubs-overview-page',
    component: StaticHomeBrusselsHubsOverview,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/events-brussels',
    name: 'brussels-events',
    component: StaticHomeBrusselsEvents,
    beforeEnter: (to, from, next) => {
      $store.getters.isBrussels || $store.getters.isMartech ? next() : next('/')
    },
  },
  {
    path: '/my-network',
    name: 'samergo-network',
    component: StaticHomeSamergoNetwork,
    beforeEnter: [mustLogin, (to, from, next) => {
      $store.getters.isSamergo || $store.getters.isMartech ? next() : next('/')
    }],
  },
  {
    path: '/regio',
    name: 'samergo-regio',
    component: ActorsSimplifiedRegio,
    beforeEnter: [mustLogin, (to, from, next) => {
      $store.getters.isSamergo || $store.getters.isMartech ? next() : next('/')
    }],
  },
  { path: '/dashboards/:panel', name: 'dashboards', component: Dashboards },
  { path: '/knowledge-base/create', name: 'knowledge-base-create', component: KnowledgeBaseCreate, beforeEnter: mustLogin },
  { path: '/knowledge-base/posts/:documentId/edit', name: 'knowledge-base-edit', component: KnowledgeBaseCreate, beforeEnter: canEditKnowledgeBasePost },
  { path: '/knowledge-base/posts/:documentId', name: 'knowledge-base-detail', component: KnowledgeBaseDetail },
  { path: '/dashboards/knowledge-base/posts/:documentId', name: 'dashboards-knowledge-base-detail', component: KnowledgeBaseDetail },
  { path: '/spotting-areas/:spottingAreaId/knowledge-base/create', name: 'spotting-area-knowledge-base-create', component: KnowledgeBaseCreate, beforeEnter: mustLogin },
  {
    path: '/spotting-areas/:spottingAreaId/knowledge-base/posts/:documentId/edit',
    name: 'spotting-area-knowledge-base-edit',
    component: KnowledgeBaseCreate,
    beforeEnter: canEditKnowledgeBasePost,
  },
  { path: '/spotting-areas/:spottingAreaId/knowledge-base/posts/:documentId', name: 'spotting-area-knowledge-base-detail', component: KnowledgeBaseDetail },

  { path: '/communities/:communityId/knowledge-base/create', name: 'community-knowledge-base-create', component: KnowledgeBaseCreate, beforeEnter: mustLogin },
  {
    path: '/communities/:communityId/knowledge-base/posts/:documentId/edit',
    name: 'community-knowledge-base-edit',
    component: KnowledgeBaseCreate,
    beforeEnter: canEditKnowledgeBasePost,
  },
  { path: '/communities/:communityId/knowledge-base/posts/:documentId', name: 'community-knowledge-base-detail', component: KnowledgeBaseDetail },
  {
    path: '/actors/:id',
    component: Actor,

    children: [
      // `/actors/:id` and `/actors/:id/classic` are deliberately one route with one name rather
      // than two routes: a dozen places key behaviour off `$route.name === 'actor-detail'` — the
      // breadcrumbs, the second top bar, the resource side panels, and UiMixin's
      // shouldShowActorSimplifiedDetailPage, which is the chooser's own second branch. An alias
      // gives the classic page an address of its own and leaves all of that intact.
      // ActorDetailVersionChooser reads the path to decide; see isClassicProfileRequested.
      { path: '', name: 'actor-detail', component: ActorDetailVersionChooser, alias: 'classic' },
      // Augmented Actor Profile.
      //
      // On Foodleap this is an alias: ActorDetailVersionChooser already renders the augmented
      // profile at /actors/:id for everyone, and the route survives for the links that point here.
      // On every other ecosystem it is still the internal preview, open to DataScouts staff and
      // Foodvalley (who run Foodleap and review the profiles) via the email allowlist in the Vuex
      // getter — the guard and any UI showing the link then share one source of truth. The page
      // cannot import from this module because the router imports the page.
      //
      // Append ?preview=visitor, or use the topbar toggle, to force the non-owner view.
      {
        path: 'aap',
        name: 'actor-detail-aap',
        component: FoodleapSimplifiedActorDetail,
        beforeEnter: canAccessAugmentedProfile,
      },
      { path: 'changes', component: ActorChanges },
      { path: 'scores', component: ActorScores },
    ],
  },
  { path: '/spotting-areas', name: 'spottingAreas', component: SpottingAreas },
  { path: '/spotting-areas/:spottingAreaId/:section?/:id?', name: 'spotting-area-detail', component: SpottingAreaDetail },
  { path: '/settings', redirect: '/settings/ecosystem', beforeEnter: defaultSettingsPage },
  { path: '/settings/ecosystem-maps/:id', name: 'ecosystem-map-detail', component: EcosystemMapDetail, beforeEnter: mustLogin },
  { path: '/settings/chatbot-widgets/:id', name: 'chatbot-widget-detail', component: ChatbotWidgetDetail, beforeEnter: mustLogin },
  { path: '/settings/:panel', name: 'settings', component: Settings, beforeEnter: mustLogin },
  { path: '/datalab', redirect: '/datalab/completion' },
  { path: '/datalab/:panel', name: 'datalab', component: Datalab, beforeEnter: mustLogin },
  { path: '/forgot-password', name: 'forgot-password', component: ForgotPassword },
  { path: '/login', name: 'login', component: SignIn },
  /*{
    path: '/dashboards/home',
    name: 'home',
    component: HomeView,
    beforeEnter: mustLogin,
  },*/
  {
    path: '/login',
    name: 'login',
    component: SignIn,
  },
  { path: '/notifications', name: 'notifications', component: Notifications },
  { path: '/notifications/:id', name: 'notifications-detail', component: Notifications },
  { path: '/announcements', name: 'announcements', component: AnnouncementsNew },
  { path: '/events-simplified', name: 'events-simplified', component: EventsNew },
  { path: '/events', name: 'events', component: EventsNew },
  { path: '/events/:id', name: 'events-detail', component: AnnouncementDetailSimplifiedNew },
  { path: '/challenges', name: 'challenges', component: Challenges },
  { path: '/challenges/:id', name: 'challenges-detail', component: ChallengeDetailNew },
  { path: '/password/reset/:token', name: 'change-password', component: ChangePassword },
  { path: '/portfolios', name: 'portfolios', component: Portfolios },
  { path: '/portfolios/:id', name: 'portfolio-detail', component: SortablePortfolioDetail, beforeEnter: mustLogin },
  { path: '/profile', redirect: '/profile/update' },
  { path: '/profile-old', redirect: '/profile-old/update' },
  { path: '/profile/:panel/:id?', name: 'profile', component: Profile, beforeEnter: mustLogin },
  { path: '/profile-old/:panel/:id?', name: 'profile-old', component: Profile, beforeEnter: mustLogin },
  { path: '/register', name: 'sign-up', component: SignUp },
  { path: '/register/confirm/:token', redirect: '/register' },
  { path: '/restricted', name: 'restricted', component: Restricted, beforeEnter: mustLogin },
  { path: '/offline', name: 'offline', component: Offline },
  { path: '/datascouts-terms-and-conditions', name: 'gdpr-terms', component: GdprTerms, beforeEnter: mustLogin },
  { path: '/settings', redirect: '/settings/ecosystem', beforeEnter: defaultSettingsPage },
  { path: '/settings/:panel', name: 'settings', component: Settings, beforeEnter: mustLogin },
  { path: '/spotting-areas', name: 'spotting-areas', component: SpottingAreas },
  { path: '/spotting-areas/:spottingAreaId/:section?/:id?', name: 'spotting-area-detail', component: SpottingAreaDetail },
  {
    path: '/announcements/:id',
    name: 'announcements-detail',
    component: AnnouncementDetailSimplifiedNew,
  },
  { path: '/transition-pulse/:id', name: 'transition-pulse-detail', component: TransitionPulseDetail, beforeEnter: [mustLogin, canAccessFoodleapReports] },
  { path: '/innovation-radar/:id', name: 'innovation-radar-detail', component: InnovationRadarDetail, beforeEnter: [mustLogin, canAccessFoodleapReports, mustHaveClaimedActor] },
  {
    path: '/deep-dive-reports/balanced-protein-futures',
    name: 'deep-dive-balanced-protein',
    component: DeepDiveReportBalancedProtein,
    beforeEnter: [mustLogin, canAccessFoodleapReports, mustHaveClaimedActor],
  },
  { path: '/deep-dive-reports/:year?', name: 'deep-dive-report-detail', component: DeepDiveReportDetail, beforeEnter: [mustLogin, canAccessFoodleapReports, mustHaveClaimedActor] },
  { path: '/info', name: 'info', component: Info },
  { path: '/terms-of-use/:id?', name: 'terms-of-use', component: Policy },
  { path: '/privacy-policy/:id?', name: 'privacy-policy', component: Policy },
  { path: '/cookie-policy/:id?', name: 'cookie-policy', component: Policy },
  { path: '/about', name: 'about', component: Policy },
]

if (import.meta.env.DEV) {
  routes.push({
    path: '/kitchen-sink',
    beforeEnter: mustLogin,
    component: KitchenSink,
  })
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  linkActiveClass: 'navigation__link--active',
  scrollBehavior () {
    document.querySelector('.main-view')?.scrollTo({ top: 0 })
  },
})

router.beforeEach(async (to, from, next) => {
  if ($store.state.ui.sidePanel.isVisible && to.path !== from.path) {
    $store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
  }

  // Directory pages let the user pick a portfolio themselves, so no portfolio is forced on
  // them. The simplified directories were missing from this list, which made a portfolio
  // member land on an arbitrary portfolio (the first one they can contribute to or explore,
  // or whatever was last persisted) after every browser refresh, while navigating in from
  // another page was fine because the active portfolio is only resolved once per page load.
  const routesWithoutForcedPortfolio = ['explore', 'addActor', 'actors-simplified', 'products-simplified']

  if ($store.getters.isPortfolioMember && !routesWithoutForcedPortfolio.includes(to.name)) {
    // Since the state is reset when the browser is refreshed, this will execute the function that fetches a portfolio
    setExplorePortfolio()
  }

  if (document.body.clientWidth < 768 && ['explore', 'settings', 'portfolios'].includes(to.name) && to.path !== $store.getters.home) {
    return next('/')
  }

  // Send users that did not accept terms yet to the datascouts-terms-and-conditions page
  if ($store.getters.isLoggedIn && !$store.getters.hasAcceptedTerms && (to.name != 'gdpr-terms' && to.path != '/profile/update') && $store.getters.isGdprDue) {
    return next('/datascouts-terms-and-conditions')
  }

  // If users go to the register (signup) and they're already signed in or canRegister is false, return to '/'
  if (to.name == 'sign-up' && $store.getters.isLoggedIn) {
    return next('/')
  }

  // If the user wants to register on an ecosystem you can't register on, without any invitation, you're not getting in
  var isCompletingRegistration = (to.redirectedFrom && to.redirectedFrom.path && to.redirectedFrom.path.includes('register/confirm/')) || to.fullPath.includes('/register?email=')

  if (to.name == 'sign-up' && !$store.getters.canRegister && !to.query.invitation && !isCompletingRegistration) {
    return next('/login')
  }

  if ($store.getters.hasAccessToActorScouting) {
    if (to.name && (to.path.startsWith('/dashboards') && (to.path === '/dashboards/knowledge-base' || to.path === '/dashboards/explore-content'))) {
      return next('/')
    }
  }

  if (!$store.getters.isLoggedIn && $store.getters.isFoodleap) {
    if (to.path === '/communities' || to.path === '/actors-simplified' || to.path === '/products-simplified') {
      return next('/')
    }
  }

  // For simplified templates, visitors and ecosystem member have access to only a few specific pages, reroute the user to the homepage if they're routed to dashboards
  if (to.name &&
    (
      (to.name.startsWith('dashboards/') && (!to.name.startsWith('dashboards/home') && !to.name.startsWith('dashboards/foodvalley-about') && !to.name.startsWith('dashboards/foodvalley-contact') && !to.name.startsWith('dashboards/knowledge-base') && !to.name.startsWith('dashboards/explore-content')) && !to.name.startsWith('static')) ||
      to.name === 'explore'
    ) &&
    [SiteTemplate.SIMPLIFIED, SiteTemplate.NEW_SIMPLIFIED].includes($store.getters.activeSiteTemplate) &&
    (!$store.getters.isLoggedIn || $store.getters.isActor)
  ) {
    return next('/')
  }

  if (to.path !== from.path) {
    try {
      trackHeapEvent('pageExit', { from: from.path, to: to.path })
    } catch (err) {
    }
  }

  if (to.name != 'gdpr-terms' && to.path !== '/profile/subscription' && $store.getters.isActor && $store.getters.hasAccessToEcosystemMemberPackages) {
    // Fetch package info and redirect to homepage if no active subscription
    if (!$store.state.user.isFetchingPackageSubscription && !$store.state.user.hasFetchedPackageSubscription) {
      await $store.dispatch(USER_ACTION_TYPES.FETCH_PACKAGE_SUBSCRIPTION)
    }

    // In case there is a promise, but fetching the info has not succeeded yet
    if ($store.state.user.fetchPackageSubscriptionPromise && !$store.state.user.hasFetchedPackageSubscription) {
      await $store.state.user.fetchPackageSubscriptionPromise
    }

    if (!$store.state.user.profile.subscriptionInfo || !$store.state.user.profile.subscriptionInfo.package) {
      return next('/profile/subscription')
    }
  }

  // Handle utm_source parameter for activity logging
  // Log when path changes OR when utm_source is present (even if same page)
  const hasUtmSource = !!to.query.utm_source
  const pathChanged = to.path !== from.path

  if (pathChanged || hasUtmSource) {
    const origin = to.query.utm_source ? String(to.query.utm_source) : ''

    setTimeout(() => {
      logUserActivity(to.path, to.name as string, to.params.id as string, origin)
    }, 5000)
  }

  next()
})

// Matomo SPA page view tracking — fires on every genuine route change after initial load.
// Skips the initial navigation (from.name is null) since Matomo auto-tracks that,
// and skips same-path navigations (hash/query changes, UTM stripping redirects).
router.afterEach((to, from) => {
  if (from.name && to.path !== from.path && window._paq) {
    window._paq.push(['setCustomUrl', to.path])
    window._paq.push(['setDocumentTitle', String(to.name || to.path)])
    window._paq.push(['trackPageView'])
    console.log('[ANALYTICS] Send!')
  }
})

router.afterEach((to, from) => {
  if (window.refreshBeforeRedirect) {
    // This is a workaround to fetch all the necessary data that comes with a new load
    window.refreshBeforeRedirect = false
    window.location.reload()
  }

  // For simplified templates, visitors and ecosystem member have access to only a few specific pages, reroute the user to the homepage if they're routed to dashboards
  if (to.name &&
    ((to.name.startsWith('dashboards/') && !to.name.startsWith('dashboards/home') && !to.name.startsWith('dashboards/foodvalley-about') && !to.name.startsWith('dashboards/foodvalley-contact') && !to.name.startsWith('static')) || to.name === 'explore') &&
    [SiteTemplate.SIMPLIFIED, SiteTemplate.NEW_SIMPLIFIED].includes($store.getters.activeSiteTemplate) &&
    (!$store.getters.isLoggedIn || $store.getters.isActor)
  ) {
    router.push('/')
  }

  // Strip UTM parameters from URL after navigation completes
  const utmParamsToStrip = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
  const hasUtmParams = utmParamsToStrip.some(param => to.query[param])

  if (hasUtmParams) {
    const query = { ...to.query }
    utmParamsToStrip.forEach(param => {
      delete query[param]
    })

    router.replace({
      path: to.path,
      query,
      params: to.params,
      hash: to.hash,
    })
  }
})

export default router
